import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { MessageSquare, Send, CheckCircle2, Search } from "lucide-react";

const CATEGORY_STYLES = {
  dispatch: "bg-indigo-50 text-indigo-700",
  pod: "bg-amber-50 text-amber-700",
};
const STATUS_STYLES = {
  open: "bg-rose-50 text-rose-700",
  answered: "bg-emerald-50 text-emerald-700",
  closed: "bg-slate-100 text-slate-700",
};

export default function Queries() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [status, setStatus] = useState("");
  const [q, setQ] = useState("");
  const [active, setActive] = useState(null);
  const [reply, setReply] = useState("");
  const isSrad = user && user.role !== "client";

  const load = async () => {
    const params = status ? { status } : {};
    const { data } = await api.get("/queries", { params });
    setItems(data);
    if (active) {
      const fresh = data.find((q) => q.id === active.id);
      if (fresh) setActive(fresh);
    }
  };
  useEffect(() => { load(); }, [status]);

  const submitReply = async () => {
    if (!active || !reply.trim()) return;
    try {
      await api.post(`/queries/${active.id}/messages`, { message: reply });
      setReply(""); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const close = async () => {
    if (!active) return;
    try {
      await api.patch(`/queries/${active.id}/status`, { status: "closed" });
      toast.success("Query closed"); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div data-testid="page-queries" className="space-y-5">
      <div className="flex items-end justify-between">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-slate-500">Support</div>
          <h1 className="font-heading text-3xl font-extrabold text-slate-900 mt-1">Queries</h1>
          <p className="text-sm text-slate-500 mt-1">
            {isSrad ? "Questions raised by client users against dispatches and PODs." : "Ask questions about your dispatches or pending PODs. SRAD team will respond here."}
          </p>
        </div>
        <select data-testid="queries-status" value={status} onChange={(e) => setStatus(e.target.value)} className="px-3 py-2 border border-slate-300 rounded-md text-sm">
          <option value="">All</option><option value="open">Open</option><option value="answered">Answered</option><option value="closed">Closed</option>
        </select>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        <div className="lg:col-span-1 bg-white border border-slate-200 rounded-lg overflow-hidden max-h-[calc(100vh-260px)] overflow-y-auto">
          <div className="flex items-center gap-2 px-3 py-2 border-b border-slate-200 bg-slate-50 sticky top-0 z-10">
            <Search size={14} className="text-slate-400" />
            <input
              data-testid="input-queries-search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search subject, dispatch, raised by…"
              className="flex-1 outline-none text-sm bg-transparent"
            />
            {q && <button onClick={() => setQ("")} className="text-xs text-slate-500 hover:text-slate-700">Clear</button>}
          </div>
          {(() => {
            const qq = q.trim().toLowerCase();
            const visible = qq
              ? items.filter((qy) => [qy.subject, qy.dispatch_code, qy.raised_by_name, qy.category, qy.status]
                  .filter(Boolean).some((v) => String(v).toLowerCase().includes(qq)))
              : items;
            return (<>
              {visible.map((qy) => (
                <button key={qy.id} data-testid={`query-row-${qy.id}`} onClick={() => setActive(qy)} className={`w-full text-left border-b border-slate-100 p-3 hover:bg-slate-50 ${active?.id === qy.id ? "bg-slate-50" : ""}`}>
                  <div className="flex items-center gap-2 mb-1">
                    <span className={`text-[10px] uppercase font-semibold px-2 py-0.5 rounded-full ${CATEGORY_STYLES[qy.category] || "bg-slate-100"}`}>{qy.category}</span>
                    <span className={`text-[10px] uppercase font-semibold px-2 py-0.5 rounded-full ${STATUS_STYLES[qy.status]}`}>{qy.status}</span>
                    <span className="text-[10px] text-slate-500 ml-auto font-mono">{qy.dispatch_code}</span>
                  </div>
                  <div className="text-sm font-semibold text-slate-800 truncate">{qy.subject}</div>
                  <div className="text-xs text-slate-500 mt-1">{qy.raised_by_name} · {new Date(qy.updated_at || qy.created_at).toLocaleString()}</div>
                </button>
              ))}
              {visible.length === 0 && <div className="p-6 text-center text-sm text-slate-500">{q ? `No matches for "${q}"` : "No queries yet."}</div>}
            </>);
          })()}
        </div>

        <div className="lg:col-span-2 bg-white border border-slate-200 rounded-lg p-5 flex flex-col min-h-[70vh]">
          {!active ? (
            <div className="m-auto text-slate-400 text-sm flex flex-col items-center gap-2">
              <MessageSquare size={40} className="opacity-40" />
              Select a query to view the conversation.
            </div>
          ) : (
            <>
              <div className="flex items-start justify-between border-b border-slate-100 pb-3 mb-3">
                <div>
                  <div className="text-xs text-slate-500 font-mono">{active.dispatch_code} · {active.category === "pod" ? "POD Query" : "Dispatch Query"}</div>
                  <div className="font-heading text-xl font-bold text-slate-900 mt-1">{active.subject}</div>
                  <div className="text-xs text-slate-500 mt-1">Raised by {active.raised_by_name} · {new Date(active.created_at).toLocaleString()}</div>
                </div>
                {isSrad && active.status !== "closed" && (
                  <button data-testid="btn-close-query" onClick={close} className="text-xs bg-slate-100 hover:bg-slate-200 text-slate-700 px-3 py-1.5 rounded-md flex items-center gap-1">
                    <CheckCircle2 size={13} /> Close
                  </button>
                )}
              </div>

              <div className="flex-1 space-y-3 overflow-y-auto pr-1">
                {(active.messages || []).map((m) => {
                  const isMine = user && m.author_id === user.id;
                  const isFromClient = m.author_role === "client";
                  return (
                    <div key={m.id} className={`flex ${isMine ? "justify-end" : "justify-start"}`}>
                      <div className={`max-w-[75%] rounded-lg px-4 py-2.5 text-sm ${isMine ? "bg-[var(--navy)] text-white" : isFromClient ? "bg-amber-50 border border-amber-200 text-slate-800" : "bg-slate-100 text-slate-800"}`}>
                        <div className="text-[10px] uppercase tracking-widest opacity-70 mb-1">{m.author_name} · {m.author_role?.replace("_", " ")}</div>
                        <div className="whitespace-pre-wrap">{m.message}</div>
                        <div className="text-[10px] opacity-60 mt-1">{new Date(m.at).toLocaleString()}</div>
                      </div>
                    </div>
                  );
                })}
              </div>

              {active.status !== "closed" ? (
                <div className="border-t border-slate-100 pt-3 mt-3">
                  <textarea data-testid="query-reply-input" rows={2} value={reply} onChange={(e) => setReply(e.target.value)} placeholder="Type your reply…" className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm resize-none" />
                  <div className="text-right mt-2">
                    <button data-testid="btn-send-reply" onClick={submitReply} disabled={!reply.trim()} className="bg-[var(--amber)] hover:bg-amber-600 text-white text-sm font-semibold px-4 py-2 rounded-md inline-flex items-center gap-2 disabled:opacity-50">
                      <Send size={13} /> Send Reply
                    </button>
                  </div>
                </div>
              ) : (
                <div className="border-t border-slate-100 pt-3 mt-3 text-center text-xs text-slate-500">This query is closed.</div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
